/**
 * Which PICTURE of a car to draw, and when (SPEC-002, SPEC-013).
 *
 * Split out of `carSchema.js` at the 350-line rule, and the seam is the one the
 * remediation names — policy against mechanism. That file is the closed
 * VOCABULARIES a content pack may draw from: the values a record is allowed to
 * say, and `isKnown` to refuse the ones it is not. This one is the decisions
 * made from them — which of a car's pictures is its portrait, which one the
 * garage grid is built from, which one travels in a link preview, and which one
 * is drawn at the moment the car is chosen.
 *
 * The two halves failed differently, which is why they are worth separating. A
 * bad vocabulary is a raw message key on screen. A bad take is an empty stage,
 * with correct markup and nothing in it.
 *
 * `carSchema.js` RE-EXPORTS everything here, so this module is where the take
 * rules are written and that one stays the single address they are read from —
 * which is the contract `scripts/lib/carSchema.js` depends on to load the whole
 * schema into the CommonJS build scripts as one object.
 *
 * Domain: pure data and pure functions, importing nothing.
 */

/**
 * Every take a car MAY carry, in the order a viewer walks them.
 *
 * `front` is a LATER delivery and behaves unlike the rest. The other takes are
 * cut-outs on transparency: the plate behind them is ours to tint, which is what
 * the `--livery-*` tokens are for. A front view arrives with its own ground —
 * a colour vignette cast to the car's own paint, yellow behind the Yellowbird,
 * red behind the Testarossa — and it is 3:2 rather than the side's ~2.5:1.
 * So it is not a drop-in alternative to `side`; it is a different picture with
 * a different job, and `CATALOGUE_TAKE` below is where that job is stated.
 */
export const GALLERY_TAKES = [
    'side',
    'front',
    'top-front',
    'top-front2',
    'top-rear',
    'rear',
    'interior',
    'engine',
    'wheel',
    'headlight',
];

/**
 * The take that plays at the moment a car is CHOSEN — the window down, and a
 * driver looking out of it.
 *
 * Deliberately NOT a member of `GALLERY_TAKES`, and it is the only take that is
 * not. Every other take is a picture OF the car and belongs in the band and in
 * the viewer beside the rest. This one is a picture of the car being TAKEN: the
 * arcade moment where the machine you picked pulls up, the glass drops and
 * somebody looks out at you. Filed in the gallery it would be the ninth
 * thumbnail of a car the reader has already walked — seen once, in a rail, and
 * never at the moment it exists for.
 *
 * It is a side profile like `side` and it is NOT a crop of one. The delivery is
 * 2:1 against the side's 3:1, framed wider to leave room over the roof, so the
 * two are not registered and cannot be cross-faded into a window that rolls
 * down. They are two pictures of the same car, and this is the one shown when
 * the car is picked.
 */
export const SELECT_TAKE = 'window';

/**
 * Every take the art build may cut — a LONGER list than the one a reader walks.
 *
 * Two lists rather than one, because `GALLERY_TAKES` had been answering two
 * questions that until now had the same answer: what may be delivered, and what
 * the gallery shows. `SELECT_TAKE` splits them. `build-car-art.js` reads this;
 * `CarGallery` and `CarViewer` read `GALLERY_TAKES`. A take added to the wrong
 * one either never gets cut at all or quietly turns up in the band.
 */
export const ART_TAKES = [...GALLERY_TAKES, SELECT_TAKE];

/**
 * A car's HERO take — the one picture the DETAIL card and every share card are
 * composed from.
 *
 * Always the side profile. It is the only take every car in the set has, it is
 * the shape the brand's own card draws, and it is the picture that reads at a
 * glance in a link preview: a front view cropped to 1200x630 loses the car's
 * length, which is the whole silhouette a 90s poster sold.
 */
export const HERO_TAKE = 'side';

/**
 * The take a car's DETAIL card and share card are actually composed from.
 *
 * `HERO_TAKE` is the intent; this is what a given car can honour. The Testarossa
 * arrived as a front view with no side profile — its side art was never in the
 * starter kit, which supplied its 1991 successor instead — so a detail screen
 * that assumed `side` would render an empty hero for it.
 *
 * Falling back rather than skipping the car is the right trade here: a front
 * view is a worse hero than a side profile, and it is very much better than a
 * hole. `canComposeShareCard` below is where the line IS drawn, because a share
 * card is seen outside the app and a 1200x630 crop of a 3:2 front view loses
 * the car.
 */
export function heroTake(car) {
    const takes = car?.media?.takes || car?.art?.takes || [];

    if (takes.includes(HERO_TAKE)) {
        return HERO_TAKE;
    }

    return takes.includes(CATALOGUE_TAKE) ? CATALOGUE_TAKE : null;
}

/**
 * The take to draw at the moment this car is chosen.
 *
 * Falls back to the hero for the same reason `catalogueTake` falls back to it:
 * the window shots are an INCREMENTAL delivery — nine of the twenty-four today —
 * and a car without one has to keep drawing its own portrait rather than a hole.
 * Which is why this is a function and not a constant: both cases coexist in one
 * deck, and the card asks rather than assumes.
 */
export function selectTake(car) {
    const takes = car?.media?.takes || car?.art?.takes || [];

    return takes.includes(SELECT_TAKE) ? SELECT_TAKE : heroTake(car);
}

/**
 * Can this car travel as a picture?
 *
 * Only off a side profile. The share shapes are 1.91:1 and 4:5, and both are
 * composed by placing the car's length across the frame — a front view cropped
 * to either loses the silhouette that is the whole reason a link preview earns
 * a second look. A car without one shares as a link, which is the same ladder
 * `shareImage` already falls down when a file cannot be attached.
 */
export function canComposeShareCard(car) {
    const takes = car?.media?.takes || car?.art?.takes || [];

    return takes.includes(HERO_TAKE);
}

/**
 * A car's CATALOGUE take — the picture the garage grid is built from.
 *
 * The front view where a car has one, because a grid of side profiles all
 * pointing the same way reads as a price list, and a wall of faces reads as a
 * garage. It is also the take that brings its own lit ground, so a catalogue
 * card can go full-bleed and drop the livery plate entirely.
 *
 * `catalogueTake` falls back to the hero rather than assuming coverage: the
 * front views are an incremental delivery, and a car without one has to keep
 * rendering. That is why this is a function and not a constant — the two cases
 * have to coexist in one grid, and the card decides its own treatment from
 * which take it got back.
 */
export const CATALOGUE_TAKE = 'front';

export function catalogueTake(car) {
    const takes = car?.media?.takes || car?.art?.takes || [];

    return takes.includes(CATALOGUE_TAKE) ? CATALOGUE_TAKE : HERO_TAKE;
}

/**
 * Does this car's catalogue picture bring its own ground?
 *
 * A front view does; a side cut-out does not and needs the livery plate under
 * it. The card reads this rather than testing the take name, so the day a
 * second self-grounded take arrives there is one place to say so.
 */
export function hasOwnGround(take) {
    return take === CATALOGUE_TAKE;
}
